import { Injectable, inject } from "@angular/core";
import { catchError, map, switchMap, takeWhile, tap, withLatestFrom } from "rxjs";

import { Actions, createEffect, ofType } from "@ngrx/effects";
// Store
import { Store } from "@ngrx/store";
import * as SportsbookActions from "../../../stores/sportsbook/sportsbook.actions";
import { SportsbookFacade } from "../../../stores/sportsbook/sportsbook.facade";
import { SportsbookState } from "../../../stores/sportsbook/sportsbook.reducers";

// Services
import { BufferFacade } from "@shared-providers/facades";
import { SportTrackPollingService } from "@sports-services/index";
import { PARAMS, SPORTS_NAVIGATION_SERVICE_ERROR_CODES } from "@sports-services/sports.navigation.service";
import { SportService } from "@sports-services/sports.service";

import { Router } from "@angular/router";
import { AlertController } from "@ionic/angular";
import { SPORTS_PATHS } from "@shared-constants/routes";
// Utils
import { SportBookService } from "@sports-services/index";
import { SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION } from "@sports-services/sports.navigation.service/sports.navigations.constants";

@Injectable({ providedIn: "root" })
export class SportsbookMarketEffects {
  // Services
  actions$ = inject(Actions);
  bufferFacade = inject(BufferFacade);
  sportService = inject(SportService);

  // Utils
  sportBookService = inject(SportBookService);
  store = inject(Store<SportsbookState>);
  sportsbookFacade = inject(SportsbookFacade);
  sportTrackPollingService = inject(SportTrackPollingService);
  router = inject(Router);
  alertController = inject(AlertController);

  timer = {
    live: "live",
    regular: "regular",
    highlights: "highlights",
    pregame: "pregame",
  };

  // MARKET PAGE
  fetchMarketCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchMarketCategories),
      switchMap((marketData: any) => {
        this.sportTrackPollingService.stopAllPollings();
        return this.sportService.getMarketCategories(marketData.NodeId).pipe(
          map((categoriesData: any) => {
            const categories = this.sportBookService.setCategories(categoriesData);
            return SportsbookActions.fetchMarkets({
              parentId: marketData.NodeId,
              leagueName: marketData.LeagueName,
              market: marketData,
              categories: categories,
              selectedCategory: categories[0],
              marketBets: [],
              betsenseData: null,
            });
          }),
          catchError((e) => {
            this.sportsbookFacade.setSportsError({
              errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
              errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_14,
            });
            return [];
          }),
        );
      }),
    ),
  );

  setMarketSelectedCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.setMarketSelectedCategory),
      withLatestFrom(this.store.select("sportsbook")),
      map(([selectedCategory, sportsbook]) => {
        const marketPageData = sportsbook["marketData"];
        return SportsbookActions.fetchMarkets({
          parentId: marketPageData.parentId,
          leagueName: marketPageData.leagueName,
          market: marketPageData.market,
          categories: marketPageData.categories,
          marketBets: marketPageData.marketBets,
          selectedCategory,
          betsenseData: null,
        });
      }),
    ),
  );

  // fetchMarkets$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(SportsbookActions.fetchMarkets),
  //     switchMap((marketPageData: any) => {
  //       this.sportTrackPollingService.stopAllPollings();
  //       return this.sportService
  //         .getMarketGamesByCategory(
  //           marketPageData.parentId,
  //           marketPageData.selectedCategory.CategoryId,
  //           marketPageData.market.SportHandle,
  //         )
  //         .pipe(
  //           map((marketBetsData: any) => {
  //              // return this.sportService
  //              //    .getMarketGamesByCategory(
  //              //      marketPageData.parentId,
  //              //      marketPageData.selectedCategory.CategoryId,
  //              //      marketPageData.market.SportHandle,
  //              //    )
  //              //    .pipe(
  //              //      map((marketBetsData: any) => {
  //             const marketBets = this.sportBookService.setMarketBets(marketBetsData, marketPageData);
  //             // const currentMarket = {
  //             //   ...marketPageData,
  //             //   marketBets,
  //             //   betsenseData: null,
  //             // };
  //             // this.sportsbookFacade.fetchMarketBetsense(currentMarket);
  //             return SportsbookActions.setMarkets({
  //               categories: marketPageData.categories,
  //               parentId: marketPageData.parentId,
  //               leagueName: marketPageData.leagueName,
  //               market: marketPageData.market,
  //               selectedCategory: marketPageData.selectedCategory,
  //               marketBets,
  //               betsenseData: null,
  //             });
  //           }),
  //           catchError((e) => {
  //             this.sportsbookFacade.setSportsError({
  //               errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
  //             });
  //             return [];
  //           }),
  //         );
  //     }),
  //     catchError((e) => {
  //       this.sportsbookFacade.setSportsError({
  //         errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
  //       });
  //       return [];
  //     }),
  //   ),
  // );

  fetchMarkets$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchMarkets),
      switchMap((marketPageData: any) => {
        this.sportTrackPollingService.stopAllPollings();
        this.sportTrackPollingService.startMarketPagePolling();
        const params = {
          parentId: marketPageData.parentId,
          categoryInfoId: marketPageData.selectedCategory.CategoryId,
          sportHandle: marketPageData.market.SportHandle,
          leagueName: marketPageData.leagueName,
        };
        return this.bufferFacade
          .newAuxBuffer(
            this.sportService,
            "getMarketGamesByCategory",
            params,
            this.timer.pregame,
            this.sportTrackPollingService.marketPolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.marketPolling$.getValue()),
            map(([_prevMarketData, currentMarketData]) => {
              const noMarkets = !Array.isArray(currentMarketData) || !currentMarketData.length;
              if (noMarkets) {
                return SportsbookActions.setSportsError({
                  errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
                  errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_26,
                });
              }
              const isNowLive = Array.isArray(currentMarketData) && currentMarketData[0]?.IsLive;
              if (isNowLive) {
                return SportsbookActions.navigateToLiveMarket(params);
              }

              const marketBets = this.sportBookService.setMarketBets(currentMarketData, marketPageData);
              return SportsbookActions.setMarkets({
                categories: marketPageData.categories,
                parentId: marketPageData.parentId,
                leagueName: marketPageData.leagueName,
                market: marketPageData.market,
                selectedCategory: marketPageData.selectedCategory,
                marketBets,
                betsenseData: null,
              });
            }),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
              });
              return [];
            }),
          );
      }),
      catchError((e) => {
        this.sportsbookFacade.setSportsError({
          errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.GENERIC_ERROR,
        });
        return [];
      }),
    ),
  );

  fetchMarketBetsense$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SportsbookActions.fetchMarketBetsense),
      switchMap((currentMarket) => {
        const marketPageDataNodeId = currentMarket?.market?.NodeId;
        this.sportTrackPollingService.startMarketPagePolling();
        return this.bufferFacade
          .newAuxBuffer(
            this.sportService,
            "getBetsenseData",
            marketPageDataNodeId,
            this.timer.regular,
            this.sportTrackPollingService.marketPolling$,
          )
          .pipe(
            takeWhile((_) => this.sportTrackPollingService.marketPolling$.getValue()),
            map(([prev, current]) =>
              SportsbookActions.setMarkets({
                ...currentMarket,
                betsenseData: current.response.results,
              }),
            ),
            catchError((e) => {
              this.sportsbookFacade.setSportsError({
                errorCode: SPORTS_NAVIGATION_SERVICE_ERROR_CODES.BETSENSE_POLLING_ERROR,
                errorDescription: SPORTS_NAVIGATION_SERVICE_ERROR_LOCATION.LOCATION_15,
              });
              return [];
            }),
          );
      }),
    ),
  );

  navigateToLiveMarket$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SportsbookActions.navigateToLiveMarket),
        tap((params) => {
          this.sportTrackPollingService.stopAllPollings();
          const formattedParams = {
            [PARAMS.NODE_ID]: params.parentId,
            [PARAMS.SPORT_HANDLE]: params.sportHandle,
            [PARAMS.NAME]: params.leagueName,
          };
          this.router.navigate([SPORTS_PATHS.NowLiveDetailPage], {
            state: { params: formattedParams },
          });
        }),
      ),
    { dispatch: false },
  );
}
